/**
 * Hook for managing sandbox detail page layout preference.
 * Persists the selected layout to localStorage.
 *
 * @module hooks/useSandboxLayout
 */

import { useState, useCallback } from 'react';

/** Available layout modes for the sandbox detail page */
export type SandboxLayout = 'classic' | 'alt';

const STORAGE_KEY = 'adc_sandbox_layout';
const VALID_LAYOUTS: SandboxLayout[] = ['classic', 'alt'];
const DEFAULT_LAYOUT: SandboxLayout = 'classic';

/**
 * Hook to manage the sandbox detail page layout preference.
 * Persists to localStorage so the choice survives page reloads.
 *
 * @returns Tuple of [currentLayout, setLayout]
 */
export function useSandboxLayout(): [SandboxLayout, (layout: SandboxLayout) => void] {
  const [layout, setLayoutState] = useState<SandboxLayout>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && VALID_LAYOUTS.includes(stored as SandboxLayout)) {
        return stored as SandboxLayout;
      }
    } catch {
      // localStorage may be unavailable
    }
    return DEFAULT_LAYOUT;
  });

  const setLayout = useCallback((newLayout: SandboxLayout) => {
    setLayoutState(newLayout);
    try {
      localStorage.setItem(STORAGE_KEY, newLayout);
    } catch {
      // localStorage may be unavailable
    }
  }, []);

  return [layout, setLayout];
}
